import { createSelectorCreator, defaultMemoize } from 'reselect';
import {
    createSelector,
    ContactFolder,
    FavoriteEntity,
    getMatchingFavorite,
    HubPersona,
    PersonType,
} from '../../shared';
import type { PersonaListEntry } from '../selectors/personaListSelector';
import {
    AppState,
    HubPersonasMap,
    getActiveLeftNavItemId,
    getFavorites,
    UnifiedGroupsMap,
} from '../../appstate';
import type { PersonaListViewModel } from '../models/PersonaListViewModel';
import { getPersonaListViewModel } from './personaListViewModelSelector';
import type { HubState } from '../state';
import type { MoveContactsState } from '../state/moveContactsState';
import {
    isPersonaMapsEqual,
    filterPersonasAndRemoveDeleted,
    applyGroupingAndSorting,
    toPersonaListElement,
    getPersonaForFavoritePersona,
    updatedWithContactsInMove,
} from './personaListSelectorUtils';

export const getPersonasByFolderId = createSelector<
    AppState,
    string,
    string[],
    HubPersonasMap,
    HubPersona[]
>(
    'getPersonasByFolderId',
    [
        (appState, folderId) =>
            (folderId && appState.data.contactFoldersState.folderIdToHubPersonaIds[folderId]) || [],
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
    ],
    (folderPersonaIds, allPersonas) => {
        return folderPersonaIds.map(personaId => allPersonas[personaId]);
    }
);

export const getCurrentFolderPersonas = createSelector<
    AppState,
    HubState,
    HubPersonasMap,
    UnifiedGroupsMap,
    PersonaListViewModel,
    string,
    HubPersona[],
    string,
    string[],
    FavoriteEntity[],
    ContactFolder,
    MoveContactsState,
    PersonaListEntry[]
>(
    'getCurrentFolderPersonas',
    [
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        getPersonaListViewModel,
        getActiveLeftNavItemId,
        appState => getPersonasByFolderId(appState, getActiveLeftNavItemId(appState)),
        (appState: AppState) => appState.data.userConfiguration.settings.UserCulture,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.deletePersonaState.personasBeingDeleted : [],
        getFavorites,
        (appState: AppState) => appState.data.contactFoldersState.rootContactFolder,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.moveContactsState : null,
    ],
    (
        personas,
        unifiedGroupsMap,
        personaListViewModel,
        activeLeftNavItemId,
        currentFolderPersonas,
        culture,
        personasBeingDeleted,
        favorites,
        rootContactFolder,
        moveContactsState
    ) => {
        let folderEntries = currentFolderPersonas;

        // Show favorites in all folder, and make sure favorites are not shown as duplicates.
        if (rootContactFolder && rootContactFolder.id === activeLeftNavItemId) {
            const filteredFavorites = favorites.filter(
                favoritePerson =>
                    !getPersonaForFavoritePersona(
                        favoritePerson,
                        personas,
                        unifiedGroupsMap,
                        folderEntries
                    )
            );
            folderEntries = [...folderEntries, ...filteredFavorites];
        }

        let entries = filterPersonasAndRemoveDeleted(
            folderEntries,
            personaListViewModel,
            personasBeingDeleted
        );
        if (moveContactsState && moveContactsState.personas.length) {
            entries = updatedWithContactsInMove(entries, moveContactsState, activeLeftNavItemId);
        }
        return applyGroupingAndSorting(entries, personaListViewModel, culture);
    }
);

export const getFavoritePersonas = createSelector<
    AppState,
    HubState,
    HubPersonasMap,
    UnifiedGroupsMap,
    FavoriteEntity[],
    string[],
    PersonaListEntry[]
>(
    'getFavoritePersonas',
    [
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        getFavorites,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.deletePersonaState.personasBeingDeleted : [],
    ],
    (personas, unifiedGroupsMap, favoritePersons, personasBeingDeleted) => {
        const favoritesIdsBeingDeleted = personasBeingDeleted
            .map(hubPersonaId => personas[hubPersonaId] || unifiedGroupsMap[hubPersonaId])
            .map(persona => getMatchingFavorite(persona, favoritePersons))
            .filter(match => !!match)
            .map(favorite => favorite.hubPersonaId);

        return favoritePersons
            .filter(favorite => !favoritesIdsBeingDeleted.includes(favorite.hubPersonaId))
            .map(
                favorite =>
                    getPersonaForFavoritePersona(favorite, personas, unifiedGroupsMap, []) ||
                    favorite
            )
            .filter(favoritePersona =>
                favoritePersona.type === PersonType.Group
                    ? !!favoritePersona.isMember
                    : favoritePersona.type !== PersonType.FavoriteUnifiedGroup
            )
            .map(toPersonaListElement);
    }
);
